import { create } from 'zustand';
import { Persistence } from '../../../platform/persistence/Persistence';
import { DifficultyProfile, GameMode } from '../../../shared/config/constants';
import type { GeneratedLevel } from '../level-system/generator/generator';

interface GameSaveData {
  maxLevelUnlocked: number;
  scores: Record<number, number>; // level -> score
  stars: Record<number, number>; // level -> stars
  difficultyProfile: DifficultyProfile;
}

const SAVE_KEY = 'eternally_find_the_number_v1';
const SAVE_VERSION = 1;

const defaultSaveData: GameSaveData = {
  maxLevelUnlocked: 1,
  scores: {},
  stars: {},
  difficultyProfile: DifficultyProfile.Normal
};

interface GameState {
  // Persisted state
  maxLevelUnlocked: number;
  scores: Record<number, number>;
  stars: Record<number, number>;
  difficultyProfile: DifficultyProfile;

  // Ephemeral play state
  currentMode: GameMode;
  currentLevel: number;
  isPaused: boolean;
  score: number;
  streak: number;
  
  // Active level data
  activeLevelData: GeneratedLevel | null;

  uiState: 'home' | 'level_select' | 'playing' | 'complete';

  // Actions
  startGame: (level: number, mode?: GameMode) => void;
  pauseGame: () => void;
  resumeGame: () => void;
  completeLevel: (level: number, score: number, stars: number) => void;
  setDifficultyProfile: (profile: DifficultyProfile) => void;
  setActiveLevelData: (data: GeneratedLevel | null) => void;
  setGameState: (state: 'home' | 'level_select' | 'playing' | 'complete') => void;
}

const initialSave = Persistence.load<GameSaveData>(SAVE_KEY, defaultSaveData, SAVE_VERSION);

export const useGameStore = create<GameState>((set, get) => ({
  ...initialSave,
  
  currentMode: GameMode.Campaign,
  currentLevel: 1,
  isPaused: false,
  score: 0,
  streak: 0,
  activeLevelData: null,
  uiState: 'home',

  startGame: (level, mode = GameMode.Campaign) => {
    set({ currentLevel: level, currentMode: mode, isPaused: false, score: 0, streak: 0, uiState: 'playing' });
  },

  pauseGame: () => set({ isPaused: true }),
  
  resumeGame: () => set({ isPaused: false }),

  completeLevel: (level, score, stars) => {
    set((state) => {
      const newScores = { ...state.scores };
      const newStars = { ...state.stars };
      
      if (!newScores[level] || score > newScores[level]) {
        newScores[level] = score;
      }
      if (!newStars[level] || stars > newStars[level]) {
        newStars[level] = stars;
      }

      const nextLevelUnlocked = Math.max(state.maxLevelUnlocked, level + 1);

      const newData: GameSaveData = {
        maxLevelUnlocked: nextLevelUnlocked,
        scores: newScores,
        stars: newStars,
        difficultyProfile: state.difficultyProfile
      };

      Persistence.save(SAVE_KEY, newData, SAVE_VERSION);

      return {
        ...newData
      };
    });
  },

  setDifficultyProfile: (profile) => {
    set({ difficultyProfile: profile });
    const { maxLevelUnlocked, scores, stars } = get();
    Persistence.save(SAVE_KEY, { maxLevelUnlocked, scores, stars, difficultyProfile: profile }, SAVE_VERSION);
  },

  setActiveLevelData: (data) => set({ activeLevelData: data }),
  setGameState: (state) => set({ uiState: state })
}));
